import {Box, Typography} from '@mui/material';
import './BasicInfo.scss';
import {t} from 'i18next';
import ArrTextField from '../../../../common/arr-text-field/ArrTextField';
import {useEffect, useMemo, useRef, useState} from 'react';
import ArrMultiSelectDropdown from '../../../../common/arr-select-dropdown/ArrMultiSelectDropdown';
import {ISelectItem} from '../../../../../shared/types';
import {useDispatch, useSelector} from 'react-redux';
import {
  setCreateCustomIndex,
  setSecurityGroupIds,
  setSelectedSecurityGroup,
} from '../../../../../redux/slice/createCustomIndexSlice';
import {RootState} from '../../../../../redux/store';
import {useSecurityGroupsRoleImpersonation} from '../../../../../hooks/users/useUsers';
import {useCustomIndexNameValidate} from '../../../../../hooks/ingestions/useIngestions';

const BasicInfo = () => {
  const dispatch = useDispatch();
  const {createCustomIndex, isReadOnlyView} = useSelector(
    (state: RootState) => state.indexManagement
  );
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const [customIndexName, setCustomIndexName] = useState<string>(
    createCustomIndex?.indexName ?? ''
  );
  const [customIndexNameBlur, setCustomIndexNameBlur] = useState<string>('');
  const [customIndexErrorMessage, setCustomIndexErrorMessage] =
    useState<string>('');
  const [blurCount, setBlurCount] = useState<number>(0);
  const handleSecurityGroups = (selected: ISelectItem[]) => {
    const securityGroupIds = selected.map((data) => Number(data.id));
    dispatch(setSecurityGroupIds(securityGroupIds));
    const selectedSecurityGroups = selected.map((group) => String(group.name));
    dispatch(setSelectedSecurityGroup(selectedSecurityGroups));
  };

  const {data: nameValidateData} = useCustomIndexNameValidate(
    customIndexNameBlur,
    blurCount
  );
  const {data: availableSecurityGroups = []} =
    useSecurityGroupsRoleImpersonation(2, Number(loginUserData?.projectId), {
      enabled: true,
    });

  useEffect(() => {
    if (nameValidateData?.isExist) {
      setCustomIndexErrorMessage(
        t('indexManagement.customIndex.basicInfo.indexNameExistMessage')
      );
      dispatch(setCreateCustomIndex({indexName: ''}));
    } else {
      dispatch(setCreateCustomIndex({indexName: customIndexName}));
      setCustomIndexErrorMessage('');
    }
  }, [nameValidateData]);

  const securityGroupOptions = useMemo<ISelectItem[]>(() => {
    return availableSecurityGroups?.map((s) => ({
      id: s.key,
      name: s.value,
    }));
  }, [availableSecurityGroups]);

  useEffect(() => {
    if (!isReadOnlyView) {
      return;
    }
    const securityGroupIds = createCustomIndex?.securityGroupIds ?? [];
    if (!securityGroupIds.length || !securityGroupOptions.length) {
      return;
    }
    dispatch(setSecurityGroupIds(securityGroupIds));
    const selectedSecurityGroups = securityGroupOptions
      .filter((group) => securityGroupIds.includes(Number(group.id)))
      .map((group) => String(group.name));
    dispatch(setSelectedSecurityGroup(selectedSecurityGroups));
  }, [
    isReadOnlyView,
    createCustomIndex?.securityGroupIds,
    securityGroupOptions,
    dispatch,
  ]);

  const handleClearSecurityGroups = () => {
    dispatch(setSecurityGroupIds([]));
    dispatch(setSelectedSecurityGroup([]));
  };

  const securityGroups = useMemo<ISelectItem[]>(() => {
    if (!createCustomIndex?.securityGroupIds?.length) {
      return [];
    }

    return securityGroupOptions.filter((item) =>
      createCustomIndex.securityGroupIds.includes(Number(item.id))
    );
  }, [securityGroupOptions, createCustomIndex?.securityGroupIds]);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCustomIndexName = (customName: string) => {
    setCustomIndexName(customName);
    if (customName === '') {
      dispatch(setCreateCustomIndex({indexName: ''}));
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }
    const regex = /^[a-zA-Z0-9 ]*$/;

    if (!regex.test(customName)) {
      setCustomIndexErrorMessage(
        t('indexManagement.customIndex.basicInfo.nameValidationMsg')
      );
      dispatch(setCreateCustomIndex({indexName: ''}));

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    setCustomIndexErrorMessage('');
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setCustomIndexNameBlur(customName);
      setBlurCount((value) => value + 1);
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const getState = () => {
    if (isReadOnlyView) {
      return 'inactive';
    }
    return customIndexErrorMessage ? 'error' : 'enabled';
  };

  return (
    <Box className="basic-info-main">
      <Box className="basic-info-container">
        <Box className="basic-info-title">
          <Typography
            className="typo-text subhead2_size title-text"
            data-testid={`basic-info-title`}
            tabIndex={0}
          >
            {t('indexManagement.customIndex.basicInfo.title')}
          </Typography>
        </Box>
        <Box className="basic-info-content">
          <Box className="text-container">
            <Box className="name-text-title">
              <Typography
                className="typo-text subhead3_size name-text"
                data-testid={`basic-info-enter-name`}
                tabIndex={0}
              >
                {t('indexManagement.customIndex.basicInfo.enterName')}
                {!isReadOnlyView && (
                  <span className="required-text" data-testid={`basic-info-`}>
                    *
                  </span>
                )}
              </Typography>
              <Typography
                className="typo-text body3_size name-index-text"
                data-testid={`basic-info-name-the-index`}
                tabIndex={0}
              >
                {t('indexManagement.customIndex.basicInfo.nameTheIndex')}
              </Typography>
            </Box>
            <ArrTextField
              className={`custom-index-field${
                isReadOnlyView ? ' custom-index-field-readonly' : ''
              }`}
              state={getState()}
              tabIndex={isReadOnlyView ? 0 : undefined}
              value={customIndexName}
              onChange={(e) => handleCustomIndexName(e.target.value)}
              placeholder={t('indexManagement.customIndex.basicInfo.name')}
              supportText={customIndexErrorMessage && customIndexErrorMessage}
              dataTestId="basic-info-number-to-assign"
              slotProps={{
                inputLabel: {shrink: false},
                input: {
                  inputProps: {
                    maxLength: 50,
                  },
                },
              }}
            />
            {!customIndexErrorMessage && (
              <Typography
                className={`typo-text body4_size no-support-text${
                  isReadOnlyView ? ' no-support-text-readonly' : ''
                }`}
                data-testid={`support-text-no-error`}
                tabIndex={0}
              >
                {t('indexManagement.customIndex.basicInfo.inputSupportText')}
              </Typography>
            )}
          </Box>
          <Box className="dropdown-container">
            <Box className="name-text-title">
              <Typography
                className="typo-text subhead3_size name-text"
                data-testid={`basic-info-select-security-group`}
                tabIndex={0}
              >
                {t('indexManagement.customIndex.basicInfo.selectSecurityGroup')}
              </Typography>
              <Typography
                className="typo-text body3_size name-index-text"
                data-testid={`basic-info-control-text`}
                tabIndex={0}
              >
                {t('indexManagement.customIndex.basicInfo.controlText')}
              </Typography>
            </Box>
            <ArrMultiSelectDropdown
              dropdownLabel={t(
                'indexManagement.customIndex.basicInfo.selectSecurityGroup'
              )}
              dropdownItems={securityGroupOptions}
              selectedValues={securityGroups}
              onSelectionChange={handleSecurityGroups}
              multiple
              searchable
              tabIndex={isReadOnlyView ? 0 : undefined}
              withControls
              checkboxDataTestId="custom-index-basic-info-security-group-dropdown-checkbox"
              onClearAll={handleClearSecurityGroups}
              onApply={() => {}}
              isShowTooltip={false}
              disabled={isReadOnlyView}
              dataTestId="msd-select-security-group"
            />
            <Box className="project-admin-container">
              <Typography
                className="typo-text subhead5_size name-text"
                data-testid={`basic-info-project-admin`}
                tabIndex={0}
              >
                {t('indexManagement.customIndex.basicInfo.projectAdmin')}
              </Typography>
              <Typography
                className="typo-text body3_size name-index-text"
                data-testid={`basic-info-project-admin-content`}
                tabIndex={0}
              >
                {t('indexManagement.customIndex.basicInfo.projectAdminContent')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BasicInfo;
